import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from './TopBar';
import { useAuth } from '../contexts/AuthContext';
import './AuthCard.css';

import API_BASE_URL from '../config/api';

const Dashboard = () => {
  const { hasPermission } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Nya lösenorden matchar inte');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setPasswordError('Lösenordet måste vara minst 8 tecken');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordSuccess('Lösenordet har ändrats');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowPasswordForm(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(data.message || data.error || 'Kunde inte ändra lösenord');
      }
    } catch (err) {
      setPasswordError('Nätverksfel vid ändring av lösenord');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const cancelPasswordChange = () => {
    setShowPasswordForm(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  return (
    <>
      <TopBar />
      <div className="dashboard ut-page">
      <div className="ut-page-head">
        <div className="ut-eyebrow">Hälsosam Urbanism</div>
        <h1>Verktyg</h1>
        <p className="ut-lede">
          Analysverktyg för att planera och utvärdera hälsofrämjande stadsmiljöer.
        </p>
      </div>

      {/* Password Change Modal — shares AuthCard.css with the login popup
          and the reset-password page, so all auth dialogues stay identical. */}
      {showPasswordForm && (
        <div
          className="auth-overlay"
          onClick={cancelPasswordChange}
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-password-title"
        >
          <div className="auth-card" onClick={(e) => e.stopPropagation()}>
            <button
              className="auth-close"
              onClick={cancelPasswordChange}
              aria-label="Stäng"
              data-tip="auth.close"
            >
              ×
            </button>

            <svg className="auth-mark" viewBox="0 0 34 28" aria-hidden="true">
              <ellipse cx="12" cy="14" rx="10" ry="11" fill="#1B5E5A" opacity="0.88" />
              <ellipse cx="21" cy="11" rx="10" ry="11" fill="#C2683E" opacity="0.82" />
              <ellipse cx="17" cy="18" rx="9.5" ry="10" fill="#5B8C5A" opacity="0.82" />
            </svg>

            <div className="auth-eyebrow">Hälsosam Urbanism</div>
            <h2 id="change-password-title" className="auth-title">Ändra lösenord</h2>
            <p className="auth-lede">Välj ett nytt lösenord på minst 8 tecken.</p>

            <form onSubmit={handleChangePassword} className="auth-form">
              {passwordError && <div className="auth-error">{passwordError}</div>}
              {passwordSuccess && <div className="auth-success">{passwordSuccess}</div>}

              <label className="auth-field">
                <span className="auth-label">Nuvarande lösenord</span>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="Ange nuvarande lösenord"
                />
              </label>

              <label className="auth-field">
                <span className="auth-label">Nytt lösenord</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder="Minst 8 tecken"
                />
              </label>

              <label className="auth-field">
                <span className="auth-label">Bekräfta nytt lösenord</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder="Upprepa nytt lösenord"
                />
              </label>

              <div className="auth-actions">
                <button type="submit" className="auth-submit" disabled={isChangingPassword} data-tip="auth.savePassword">
                  {isChangingPassword ? 'Ändrar…' : 'Ändra lösenord'}
                </button>
                <button type="button" className="auth-secondary" onClick={cancelPasswordChange} data-tip="auth.cancel">
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="services-grid">
        <Link to="/walking-calculator" className="service-tile r1">
          <div className="service-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7"/>
            </svg>
          </div>
          <h3>Promenadavstånd</h3>
          <p>Hitta vad som finns inom 10 minuters promenad från valfri plats i Stockholm.</p>
          <span className="service-status active">Tillgänglig</span>
        </Link>

        <Link to="/street-analysis" className="service-tile r2">
          <div className="service-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
            </svg>
          </div>
          <h3>Gatuanalys</h3>
          <p>Analysera gatumiljöer och fasader med AI för att mäta visuellt intresse.</p>
          <span className="service-status active">Tillgänglig</span>
        </Link>

        {/* Gated: only users with open_space:view (admins bypass) see this tile. */}
        {hasPermission('open_space:view') && (
          <Link to="/open-space" className="service-tile r3">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm3 3h8v8H8V8z" fillRule="evenodd"/>
              </svg>
            </div>
            <h3>Tomma ytor</h3>
            <p>Hitta byggnadsfria ytor större än 45 × 45 m var som helst i Sverige.</p>
            <span className="service-status active">Tillgänglig</span>
          </Link>
        )}

        {/* Gated: only users with space_syntax:view (admins bypass) see this tile. */}
        {hasPermission('space_syntax:view') && (
          <Link to="/space-syntax" className="service-tile r3">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" width="48" height="48">
                <path d="M3 7h18M3 17h18M7 3v18M17 3v18M7 12l10 5"/>
              </svg>
            </div>
            <h3>Gatunät</h3>
            <p>Se hur gatunätet hänger ihop: vilka gator som är lätta att nå och vilka som bär flödena genom området (space syntax).</p>
            <span className="service-status active">Tillgänglig</span>
          </Link>
        )}

        <Link to="/dp-analysis" className="service-tile r3">
          <div className="service-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            </svg>
          </div>
          <h3>DP-Analys</h3>
          <p>Analysera detaljplaner och byggnadsdokumentation för hälsofrämjande livsmiljöer.</p>
          <span className="service-status active">Tillgänglig</span>
        </Link>

        {/* Gated: only users with urban_wiki:view (admins bypass) see this tile. */}
        {hasPermission('urban_wiki:view') && (
          <Link to="/urban-wiki" className="service-tile r4">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>
              </svg>
            </div>
            <h3>Urban Wiki</h3>
            <p>Sammanställd tvåspråkig kunskapsbas och ordlista från forskningsdokument.</p>
            <span className="service-status active">Tillgänglig</span>
          </Link>
        )}
      </div>

      <div className="dashboard-footer">
        <button
          onClick={() => setShowPasswordForm(true)}
          className="change-password-link"
          data-tip="dash.changePassword"
        >
          Ändra lösenord
        </button>
        <Link to="/" className="back-link" data-tip="dash.back">Tillbaka till startsidan</Link>
      </div>
      </div>
    </>
  );
};

export default Dashboard;
